import React, { useEffect, useState, useCallback, useReducer } from 'react';

import { EnterpriseDashboardTestIds } from '@trello/webdriverio-tests';
import { Button, Checkbox, Tooltip } from '@trello/nachos';

import {
  PendingTeamsSortOption,
  PendingTeamsClassicSortOptions,
  ClassicItemListPageSize,
} from '@trello/enterprise';

import { InviteRestrictValues } from 'app/scripts/views/organization/constants.coffee';
import Alerts from 'app/scripts/views/lib/alerts.coffee';

import { Spinner } from 'app/src/components/Spinner';
import { InfiniteList } from 'app/src/components/InfiniteList';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { Overlay, OverlayBackground } from 'app/src/components/Overlay';
import {
  forNamespace,
  forTemplate,
  localizeCount,
} from 'app/common/lib/util/i18n';
import { PageHeader } from './PageHeader';
import { Search } from './Search';
import { PageHeaderSort } from './PageHeaderSort';
import { Tabs, ClassicLoadingStates } from './constants';
import { PendingTeamItem, PendingTeam } from './PendingTeamItem';

import styles from './PendingTeamsPage.less';

const format = forTemplate('enterprise_pending_teams');
const formatDangerous = forTemplate('enterprise_pending_teams', {
  shouldEscapeStrings: false,
});
const formatTitle = forNamespace('view title');
const formatSortSelected = forTemplate('enterprise_list_header_component');
const formatSortMenu = forTemplate('enterprise_dashboard_single_line_filter');

const SortOptions = [
  {
    name: PendingTeamsSortOption.AZ,
    menuDisplay: formatSortMenu('A-Z'),
    selectedDisplay: formatSortSelected(['displayName', 'ascending']),
  },
  {
    name: PendingTeamsSortOption.ZA,
    menuDisplay: formatSortMenu('Z-A'),
    selectedDisplay: formatSortSelected(['displayName', 'descending']),
  },
  {
    name: PendingTeamsSortOption.LeastRecentlyCreated,
    menuDisplay: formatSortMenu('least-recently-created'),
    selectedDisplay: formatSortSelected(['dateCreated', 'ascending']),
  },
  {
    name: PendingTeamsSortOption.MostRecentlyCreated,
    menuDisplay: formatSortMenu('most-recently-created'),
    selectedDisplay: formatSortSelected(['dateCreated', 'descending']),
  },
];

enum OverlayKind {
  Approve,
  Decline,
}

const useOverlays = () => {
  // This is implemented using useReducer instead of just having
  // two separate useStates for activeOverlay and activeTeam because
  // the two values need to be set and unset together.
  // Having only one of the values set is an error state, and shouldn't
  // be possible.
  const [{ activeOverlay, activeTeam }, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'close':
          return { activeOverlay: null, activeTeam: null };
        case 'open':
          return {
            activeOverlay: action.payload.kind,
            activeTeam: action.payload.team,
          };
        default:
          return state;
      }
    },
    { activeOverlay: null, activeTeam: null },
  );

  const openApproveOverlay = useCallback((team: PendingTeam) => {
    dispatch({ type: 'open', payload: { kind: OverlayKind.Approve, team } });
  }, []);

  const openDeclineOverlay = useCallback((team: PendingTeam) => {
    dispatch({ type: 'open', payload: { kind: OverlayKind.Decline, team } });
  }, []);

  const closeOverlay = useCallback(() => {
    dispatch({ type: 'close' });
  }, []);

  return {
    activeOverlay,
    activeTeam,
    openApproveOverlay,
    openDeclineOverlay,
    closeOverlay,
  };
};

/*
 * Utility hook that exposes a Set style interface, and
 * triggers React state updates when changes are made to the
 * Set
 */
const useSet = <T extends {}>(): [Set<T>, (t: T) => void, (t: T) => void] => {
  const [items, setItems] = useState(new Set<T>());

  const add = useCallback(
    (item: T): void => {
      const newItems = new Set(items);
      newItems.add(item);
      setItems(newItems);
    },
    [items],
  );

  const _delete = useCallback(
    (item: T): void => {
      const newItems = new Set(items);
      newItems.delete(item);
      setItems(newItems);
    },
    [items],
  );

  return [items, add, _delete];
};

/*
    The intention of this component is that it be as data-agnostic as possible, so that
    when we make the transition to Apollo/GraphQL, we need to change as little of it
    as possible.

    Any interfacing with classic-related-things is done via the PendingTeamsPageAdapter.
    When the time comes to move the data for this page over to Apollo, we can either
    re-work the adapter to provide data via Apollo hooks instead (and give it a more appropriate name),
    or we could just inline that data fetching directly into the PendingTeamsPage, and remove any
    sort of container layer completely. Or something completely different, but in any case,
    the core logic of the page should hopefully require few changes.
*/
interface PendingTeamsPageProps {
  loading?: boolean;
  loadMore?: () => void;
  isFiltering?: boolean;
  teams: PendingTeam[];
  enterprise: {
    displayName: string;
    totalMembers: number;
    maxMembers: number | null;
    restrictedToManagedMembers?: boolean;
    autoJoinOrganizations: boolean;
  };
  totalCount: number;
  onSearch: (query: string) => void;
  onShowAll: () => void;
  onApprove: (pendingTeam: PendingTeam) => Promise<void>;
  onDecline: (pendingTeam: PendingTeam) => Promise<void>;
  onSort: (sortOptionName: PendingTeamsSortOption) => void;
  selectedSortOptionName: string;
  onSetAutoJoinOrganizations: (value: boolean) => void;
}

export const PendingTeamsPage = (props: PendingTeamsPageProps) => {
  const {
    loading = false,
    loadMore,
    isFiltering,
    teams,
    totalCount,
    onApprove,
    onDecline,
    onSearch,
    onShowAll,
    onSetAutoJoinOrganizations,
    enterprise,
    onSort,
    selectedSortOptionName,
  } = props;

  const {
    activeOverlay,
    activeTeam,
    openApproveOverlay,
    openDeclineOverlay,
    closeOverlay,
  } = useOverlays();

  const [loadingTeams, addLoadingTeam, deleteLoadingTeam] = useSet<
    PendingTeam
  >();

  if (
    (activeOverlay !== null && activeTeam === null) ||
    (activeOverlay === null && activeTeam !== null)
  ) {
    throw new Error('Invalid pending teams overlay state');
  }

  const onClickApprove = async (t: PendingTeam) => {
    if (t.newBillableMembers.length > 0 || t.restrictedMembers.length > 0) {
      openApproveOverlay(t);
    } else {
      addLoadingTeam(t);
      await onApprove(t);
      deleteLoadingTeam(t);
    }
  };

  const onClickDecline = async (t: PendingTeam) => {
    openDeclineOverlay(t);
  };

  const onApproveFromOverlay = async (t: PendingTeam) => {
    addLoadingTeam(t);
    await onApprove(t);
    deleteLoadingTeam(t);

    // Protect against a possible case where the user has opened
    // another popover while an approve request is still in progress
    // from a previously open popover
    if (activeTeam === t) {
      closeOverlay();
    }
  };

  const onDeclineFromOverlay = async (t: PendingTeam) => {
    addLoadingTeam(t);
    await onDecline(t);
    deleteLoadingTeam(t);

    // Protect against a possible case where the user has opened
    // another popover while a decline request is still in progress
    // from a previously open popover
    if (activeTeam === t) {
      closeOverlay();
    }
  };

  return (
    <>
      <PageHeader
        title={formatTitle(Tabs.PENDING_TEAMS)}
        description={format('description', {
          enterpriseName: enterprise.displayName,
        })}
        count={teams.length}
        totalCount={totalCount}
        isFiltering={isFiltering}
        isLoading={loading}
        onShowAll={onShowAll}
        sortComponent={
          <PageHeaderSort
            sortOptions={SortOptions}
            onClickSortOption={onSort}
            selectedSortOptionName={selectedSortOptionName}
          />
        }
        searchComponent={
          <Search
            debounce
            onChange={onSearch}
            searchCount={teams.length}
            placeholder={format('search-placeholder')}
          />
        }
      />
      {enterprise.restrictedToManagedMembers && (
        <AutoJoinForm
          enterpriseDisplayName={enterprise.displayName}
          enterpriseAutoJoinEnabled={enterprise.autoJoinOrganizations}
          onChange={onSetAutoJoinOrganizations}
        />
      )}
      <hr />
      <InfiniteList
        itemCount={teams.length}
        loadMore={() => loadMore && loadMore()}
        awaitMore={!loading}
        pageSize={ClassicItemListPageSize}
        isLoading={loading}
        itemsRenderer={(items, ref) => (
          <div ref={(div: HTMLDivElement) => ref(div)}>{items}</div>
        )}
        renderItem={(index, key) => {
          const team = teams[index];
          return (
            <PendingTeamItem
              {...team}
              enterprise={enterprise}
              key={team.id}
              onApprove={() => onClickApprove(team)}
              onDecline={() => onClickDecline(team)}
              isLoading={loadingTeams.has(team)}
            />
          );
        }}
      />
      {!loading && teams.length === 0 && (
        <div className={styles.emptyState}>{format('no-results')}</div>
      )}
      <hr />
      {activeOverlay === OverlayKind.Approve && activeTeam && (
        <ApproveOverlay
          team={activeTeam}
          isLoading={loadingTeams.has(activeTeam)}
          enterprise={enterprise}
          onClose={closeOverlay}
          onApprove={() => onApproveFromOverlay(activeTeam)}
        />
      )}
      {activeOverlay === OverlayKind.Decline && activeTeam && (
        <DeclineOverlay
          isLoading={loadingTeams.has(activeTeam)}
          onClose={closeOverlay}
          onDecline={() => onDeclineFromOverlay(activeTeam)}
        />
      )}
    </>
  );
};

interface AutoJoinFormProps {
  enterpriseDisplayName: string;
  enterpriseAutoJoinEnabled: boolean;
  onChange: (value: boolean) => void;
}

const AutoJoinForm = (props: AutoJoinFormProps) => {
  const { enterpriseDisplayName, enterpriseAutoJoinEnabled, onChange } = props;

  const _onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = evt.currentTarget.checked;
    onChange(newValue);
  };
  const label = (
    <span className={styles.autoJoinLabel}>
      <p>
        {format('auto-join-description', {
          enterpriseDisplayName: enterpriseDisplayName,
        })}
      </p>
      <Tooltip
        content={
          <div>
            <p>
              {format('auto-join-tooltip', {
                enterpriseDisplayName: enterpriseDisplayName,
              })}
            </p>
            <p>{format('auto-join-tooltip-warning')}</p>
          </div>
        }
        tag="span"
      >
        <Icon small name={IconName.Information} />
      </Tooltip>
    </span>
  );
  return (
    <div
      className={styles.autoJoin}
      data-test-id={EnterpriseDashboardTestIds.AutoJoinCheckbox}
    >
      <Checkbox
        label={label}
        isChecked={enterpriseAutoJoinEnabled}
        onChange={_onChange}
      />
    </div>
  );
};

interface ApproveOverlayProps {
  team: PendingTeam;
  isLoading: boolean;
  enterprise: {
    displayName: string;
    totalMembers: number;
    maxMembers: number | null;
  };
  onClose: () => void;
  onApprove: () => void;
}
const ApproveOverlay = (props: ApproveOverlayProps) => {
  const { isLoading, onClose, onApprove, team, enterprise } = props;

  return (
    <Overlay onClose={onClose} background={OverlayBackground.DARK}>
      <div className={styles.overlay}>
        <h2>{format('are-you-sure-approve')}</h2>
        <p>
          {formatDangerous('adding-the-team', {
            teamName: team.displayName,
            enterpriseName: enterprise.displayName,
          })}
        </p>
        <ul>
          {team.newBillableMembers.length > 0 && enterprise.maxMembers && (
            <li
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: format(
                  'new-billable-members-will-be-added-with-limit',
                  {
                    numNewBillableMembers: localizeCount(
                      'new billable members',
                      team.newBillableMembers.length,
                    ),
                    numSeatsTaken: String(
                      enterprise.totalMembers + team.newBillableMembers.length,
                    ),
                    numTotalSeats: String(enterprise.maxMembers),
                  },
                ),
              }}
            />
          )}
          {team.newBillableMembers.length > 0 && !enterprise.maxMembers && (
            <li>
              <b>
                {format('new-billable-members-will-be-added', {
                  numNewBillableMembers: localizeCount(
                    'new billable members',
                    team.newBillableMembers.length,
                  ),
                })}
              </b>
            </li>
          )}
          {team.restrictedMembers.length > 0 && (
            <li
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: format('restricted-members-will-be-deactivated', {
                  numRestrictedMembers: localizeCount(
                    'restricted members',
                    team.restrictedMembers.length,
                  ),
                  teamName: team.displayName,
                  enterpriseName: enterprise.displayName,
                }),
              }}
            />
          )}
        </ul>
        <div className={styles.actions}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <Button onClick={onClose}>{format('cancel')}</Button>
              <Button
                appearance="primary"
                onClick={onApprove}
                data-test-id={
                  EnterpriseDashboardTestIds.PendingTeamItemOverlayApproveButton
                }
              >
                {format('approve-team')}
              </Button>
            </>
          )}
        </div>
      </div>
    </Overlay>
  );
};

interface DeclineOverlayProps {
  isLoading: boolean;
  onClose: () => void;
  onDecline: () => void;
}
const DeclineOverlay = (props: DeclineOverlayProps) => {
  const { onClose, isLoading, onDecline } = props;
  return (
    <Overlay onClose={onClose} background={OverlayBackground.DARK}>
      <div className={styles.overlay}>
        <h2>{format('are-you-sure-decline')}</h2>
        <p>{format('declining-request')}</p>
        <div className={styles.actions}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <Button onClick={onClose}>{format('cancel')}</Button>
              <Button
                appearance="primary"
                onClick={onDecline}
                data-test-id={
                  EnterpriseDashboardTestIds.PendingTeamItemOverlayDeclineButton
                }
              >
                {format('decline-team')}
              </Button>
            </>
          )}
        </div>
      </div>
    </Overlay>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
interface PendingTeamsPageAdapterProps {
  itemList: any;
  onSearch: (query: string) => void;
  onShowAll: () => void;
  listenTo: (model: any, event: string, fn: () => void) => void;
  reloadOrgItemLists: () => void;
  enterprise: any;
  onSort: (sortField: string, sortOrder: string) => void;
}

/**
 * The PendingTeamsPageAdapter accepts classic-specific
 * objects (ItemLists, Backbone models, etc), and renders
 * the data-agnostic PendingTeamsPage component using
 * those objects. This component will eventually be replaced
 * when the dashboard is ready to move to GraphQL.
 */
export const PendingTeamsPageAdapter = (
  props: PendingTeamsPageAdapterProps,
) => {
  const {
    itemList,
    enterprise,
    onSearch,
    onShowAll,
    onSort,
    listenTo,
    reloadOrgItemLists,
  } = props;

  const teams = itemList.items().map((i: any) => {
    return {
      id: i.id,
      displayName: i.displayName,
      requestorName: i.memberRequestor && i.memberRequestor.fullName,
      requestDate: new Date(i.date),
      logoUrl: i.logoUrl ? `${i.logoUrl}/30.png` : undefined,
      numMembers: i.membershipCount,
      newBillableMembers:
        i.transferability && i.transferability.newBillableMembers
          ? i.transferability.newBillableMembers.map((m: any) => ({
              id: m._id,
              fullName: m.fullName,
              username: m.username,
              avatarUrl: m.avatarUrl,
              initials: m.initials,
            }))
          : [],
      restrictedMembers:
        i.transferability && i.transferability.restrictedMembers
          ? i.transferability.restrictedMembers.map((m: any) => ({
              id: m._id,
              fullName: m.fullName,
              username: m.username,
              avatarUrl: m.avatarUrl,
              initials: m.initials,
            }))
          : [],
    };
  });

  const _onApprove = async (team: PendingTeam) => {
    const originalItem = itemList.items().find((i: any) => i.id === team.id);
    if (originalItem) {
      return enterprise.acceptPendingOrganization(originalItem);
    }
  };

  const _onDecline = async (team: PendingTeam) => {
    const originalItem = itemList.items().find((i: any) => i.id === team.id);
    if (originalItem) {
      return enterprise.declinePendingOrganization(originalItem);
    }
  };

  const selectedSortOption = Object.values(PendingTeamsClassicSortOptions).find(
    (so) =>
      so.sortField === itemList.getSortField() &&
      so.sortOrder === itemList.getSortOrder(),
  );
  if (!selectedSortOption) {
    throw new Error(
      'Sort field and order provided by pending teams item list does not match any sorting options',
    );
  }
  const selectedSortOptionName = selectedSortOption.name;

  const _onSort = (sortOptionName: PendingTeamsSortOption) => {
    const sortOption = PendingTeamsClassicSortOptions[sortOptionName];
    if (!sortOption) {
      throw new Error(
        'Sort option selected did not match any Pending Teams sorting options',
      );
    }
    onSort(sortOption.sortField, sortOption.sortOrder);
  };

  /*
    The state of the autojoin checkbox is managed locally so that
    the user gets immediate feedback in the UI, while the request to
    toggle the autoJoin setting on the server is still pending
  */
  const [autoJoinEnabled, setAutoJoinEnabled] = useState(
    enterprise.get('prefs')?.autoJoinOrganizations,
  );
  const [autoJoinLoading, setAutoJoinLoading] = useState(false);

  useEffect(() => {
    listenTo(enterprise, 'change:prefs', () => {
      setAutoJoinEnabled(enterprise.get('prefs')?.autoJoinOrganizations);
    });
  }, [enterprise, listenTo]);

  const onSetAutoJoinOrganizations = (value: boolean) => {
    const oldAutoJoinValue = autoJoinEnabled;
    if (value) {
      setAutoJoinLoading(true);
    }
    setAutoJoinEnabled(value);
    enterprise.setPref('autoJoinOrganizations', value, (err: any) => {
      if (err) {
        const errorMessage = err.localizedMessage?.();
        if (errorMessage) {
          Alerts.showLiteralText(errorMessage, 'error', 'alert', 5000);
        }
        setAutoJoinLoading(false);
        setAutoJoinEnabled(oldAutoJoinValue);
        return;
      }
      if (value) {
        reloadOrgItemLists();
      }
      setAutoJoinLoading(false);
    });
  };

  const loading =
    autoJoinLoading ||
    itemList.loadingState === ClassicLoadingStates.LOADING_NEW ||
    itemList.loadingState === ClassicLoadingStates.LOADING_NEXT;

  // If the auto-join: true setting change request is in progress,
  // the list of pending teams is soon going to be re-fetched. When this happens,
  // the list of pending teams is going to be empty, so here we pretend it's empty
  // right when the request is made, so the loading state isn't so jarring for the user.
  const hideTeams = loading && autoJoinLoading;

  return (
    <PendingTeamsPage
      loading={loading}
      loadMore={() => itemList?.loadNextPage()}
      teams={hideTeams ? [] : teams}
      enterprise={{
        displayName: enterprise.get('displayName'),
        totalMembers: enterprise.getTotalMembers(),
        maxMembers: enterprise.getMaxMembers(),
        restrictedToManagedMembers: enterprise
          .get('organizationPrefs')
          ?.orgInviteRestrict?.includes(InviteRestrictValues.MANAGED),
        autoJoinOrganizations: autoJoinEnabled,
      }}
      totalCount={itemList.totalLength()}
      onApprove={_onApprove}
      onDecline={_onDecline}
      onSearch={onSearch}
      isFiltering={itemList.getActiveFilters().length > 0}
      onShowAll={onShowAll}
      onSort={_onSort}
      selectedSortOptionName={selectedSortOptionName}
      onSetAutoJoinOrganizations={onSetAutoJoinOrganizations}
    />
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */
